import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FaTrashAlt } from "react-icons/fa";
import { StandardSlidingPillNav } from "../../../components";
import { deleteOwnBooks, getAllOwnBooks } from "../../../controller/addBook/premium/ownBooksController";

const READER_STATE_KEY = "own-book-reader-state";
const LAST_OPENED_BOOK_KEY = "own-book-last-opened";

const Wrapper = styled.section`
  width: 100%;
  padding: 2rem;
  background: var(--white);

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .resume-btn {
    border: 1px solid #d8b36b;
    background: #fff8ea;
    color: #6f4f1f;
    border-radius: 999px;
    padding: 0.42rem 0.9rem;
    font-size: 0.84rem;
    font-weight: 700;
    cursor: pointer;
  }

  .book-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }

  .book-card {
    position: relative;
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .book-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.16);
  }

  .cover {
    height: 170px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--primary-100);
    color: var(--grey-500);
  }

  .cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .meta {
    padding: 0.85rem 1rem 1rem;
  }

  .title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--grey-800);
  }

  .author {
    margin: 0.4rem 0 0;
    font-size: 0.86rem;
    color: var(--grey-700);
  }

  .delete-btn {
    position: absolute;
    right: 0.65rem;
    top: 0.65rem;
    border: none;
    background: rgba(220, 38, 38, 0.95);
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  }

  .status {
    margin-top: 1rem;
  }

  .empty {
    min-height: 55vh;
    display: grid;
    place-items: center;
    text-align: center;
    padding: 1rem;
  }

  .cta-btn {
    border: none;
    background: var(--primary-500);
    color: #fff;
    border-radius: var(--borderRadius);
    padding: 0.75rem 1.6rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
  }
`;

const getOwnBookId = (entry) => entry?._id || entry?.id || entry?.bookId;
const getBookPayload = (entry) => entry?.book || entry || {};

const getReaderStateMap = () => {
  try {
    const raw = localStorage.getItem(READER_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const OwnBooksList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [mode, setMode] = useState("browse");

  const { ownBooks, isLoading, error } = useSelector((store) => store.ownBooks);

  useEffect(() => {
    dispatch(getAllOwnBooks());
  }, [dispatch]);

  const books = useMemo(() => (Array.isArray(ownBooks) ? ownBooks : []), [ownBooks]);

  const lastReadInfo = useMemo(() => {
    const lastBookId = localStorage.getItem(LAST_OPENED_BOOK_KEY);
    if (!lastBookId) return null;
    const stateMap = getReaderStateMap();
    const readerState = stateMap[lastBookId];
    const match = books.find((entry) => String(getOwnBookId(entry)) === String(lastBookId));
    if (!match) return null;
    return {
      id: lastBookId,
      title: getBookPayload(match)?.title || "book",
      page: readerState?.currentPage || 1,
    };
  }, [books]);

  const onCardClick = (entry) => {
    const id = getOwnBookId(entry);
    if (!id) return;
    navigate(`/own-books/${id}`, { state: { book: entry } });
  };

  const onDelete = async (entry) => {
    const id = getOwnBookId(entry);
    if (!id) return;
    await dispatch(deleteOwnBooks([id]));
  };

  if (!isLoading && books.length === 0) {
    return (
      <Wrapper>
        <div className="empty">
          <div>
            <h3>No books curated yet</h3>
            <button className="cta-btn" onClick={() => navigate("/")}>Curate New Book</button>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="header">
        <h3>My Curated Books</h3>
        <div className="header-actions">
          {lastReadInfo && (
            <button
              type="button"
              className="resume-btn"
              onClick={() => navigate(`/own-books/${lastReadInfo.id}`)}
            >
              Resume {lastReadInfo.title} (Page {lastReadInfo.page})
            </button>
          )}
          <StandardSlidingPillNav
            options={[
              { label: "Browse", value: "browse" },
              { label: "Delete", value: "delete" },
            ]}
            active={mode}
            onChange={setMode}
          />
        </div>
      </div>

      {isLoading && <p className="status">Loading books...</p>}
      {error && <p className="status">{error}</p>}

      <div className="book-grid">
        {books.map((entry) => {
          const id = getOwnBookId(entry);
          const book = getBookPayload(entry);
          const image = book?.image || book?.formats?.["image/jpeg"];
          const title = book?.title || "Untitled";
          const author = book?.author || book?.authors?.[0]?.name || "Unknown";

          return (
            <article className="book-card" key={id} onClick={() => onCardClick(entry)}>
              {mode === "delete" && (
                <button
                  className="delete-btn"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(entry);
                  }}
                >
                  <FaTrashAlt />
                </button>
              )}

              <div className="cover">
                {image ? <img src={image} alt={title} /> : <span>No Image</span>}
              </div>

              <div className="meta">
                <p className="title">{title}</p>
                <p className="author">Author: {author}</p>
              </div>
            </article>
          );
        })}
      </div>
    </Wrapper>
  );
};

export default OwnBooksList;
